import { useHistory } from 'react-router-dom';
import { Box, SxProps, Theme, Typography, Grid } from '@mui/material';
import { FormProvider, UseFormReturn, useFieldArray } from 'react-hook-form';
import { ROUTES } from 'routing/routes';
import { useAuthState } from 'shared/stores';
import { useUploadQuoteFiles } from 'shared/mutations/pdf-upload-mutation';
import { QuotingNavigationHeader } from '../quoting-navigation-header';
import { addPdfExtension, hashFileName, formatDateTime } from 'shared/utility';
import { QuoteDocument, QuoteDocumentWithoutId, RepQuoteFormState, QuoteDoc } from 'shared/types/quotes';
import { useCreateQuote, useEditQuote } from 'shared/mutations/quotes';
import { LeaveFormDialog } from './leave-form-dialog';
import {
  CompetitionInfo,
  JobInfo,
  PlumberInfo,
  WholesalerInfo,
  ProductsInfo,
  Notes,
  QuoteDocumentation,
} from 'quoting/new-quote/form-sections';
import { useDeletePdfFile } from 'shared/mutations/pdf-delete-mutation';
import { useSnackbar } from 'notistack';
import { useUserPermissions } from 'shared/hooks';
import { PERMISSIONS, US_State } from 'shared/constants';

const alertStyles: SxProps<Theme> = {
  border: '1px solid #0288d1',
  borderRadius: '5px',
  color: '#0288d1',
  backgroundColor: '#e8f4fd',
  padding: 1,
  marginTop: 2,
  marginBottom: {
    xs: 1,
    md: 5,
  },
};

interface Props {
  methods: UseFormReturn<RepQuoteFormState>;
  quote?: QuoteDocument;
}

export function BasicQuoteForm({ methods, quote }: Props) {
  const {
    handleSubmit,
    setError,
    control,
    watch,
    formState: { isSubmitted, isSubmitting, dirtyFields },
    clearErrors,
  } = methods;
  const { fields } = useFieldArray({
    control,
    name: 'products',
  });
  const history = useHistory();
  const createQuoteMutation = useCreateQuote();
  const editQuoteMutation = useEditQuote();
  const uploadPdfMutation = useUploadQuoteFiles();
  const deletePdfMutation = useDeletePdfFile();
  const { enqueueSnackbar } = useSnackbar();
  const authState = useAuthState();
  const userPermissions = useUserPermissions();
  const hasFileAccessPermission: boolean =
    userPermissions.includes(PERMISSIONS['write']) || userPermissions.includes(PERMISSIONS['global.write']);

  const isDraft = watch('status') === 'DRAFT';

  const isDirtyAlt = !!Object.keys(dirtyFields).length;

  const onSubmit = async (data: RepQuoteFormState) => {
    try {
      //make the form data in uppercase
      Object.keys(data).forEach((key: string) => {
        if (typeof data[key as keyof RepQuoteFormState] === 'string' && key !== 'wholesalerEmail' && key !== 'id') {
          (data[key as keyof RepQuoteFormState] as string) = (data[
            key as keyof RepQuoteFormState
          ] as string)?.toUpperCase();
        }
      });

      if (!hasFileAccessPermission && (!!data.quoteFilesToDelete || data?.files)) {
        throw new Error('User does not have data load permission.');
      }

      if (!!data.quoteFilesToDelete) {
        data.quoteFilesToDelete.forEach(async file => {
          await deletePdfMutation.mutateAsync(addPdfExtension(file.id));
        });
      }

      const currentDate = new Date().toISOString();
      const userFullName = authState.user.value?.displayName.toUpperCase() || '';
      const userEmail = authState.user.value?.email.toUpperCase() || '';

      if (data.id && !!quote) {
        const quoteIdsMarkedForDeletion = !!data.quoteFilesToDelete ? data.quoteFilesToDelete.map(q => q.id) : [];
        let updatedQuoteDocumentation: QuoteDoc[] = [
          ...quote.quoteDocumentation.filter(q => !quoteIdsMarkedForDeletion.includes(q.id)),
        ];
        if (data?.files) {
          for (let i = 0; i < data?.files?.length; i++) {
            const fileDate = formatDateTime(new Date().getTime());
            const isoDate = new Date().toISOString();
            handleFiles(data, i, userFullName, isoDate);
            updatedQuoteDocumentation.push({
              id: hashFileName(`${data.id}${data.files?.[i].name || ''}${isoDate}`),
              documentType: 'ADDITIONAL DOCUMENTATION',
              documentName: data.files?.[i].name || '',
              user: userFullName,
              uploadDate: fileDate,
            });
          }
        }
        const updatedStatus =
          quote.status === 'IN PROCESS' || quote.status === 'APPROVED' || quote.status === 'REVIEWED'
            ? 'REVISION REQUESTED'
            : data.status;
        const isDraftSavedAsQuote = quote.status === 'DRAFT' && data.status === 'SUBMITTED & RECEIVED';

        const editableQuote: QuoteDocument = {
          ...quote,
          ...data,
          products: [...data.products.map(product => ({ ...product, brand: data.brand }))],
          quoteDocumentation: updatedQuoteDocumentation,
          competitionInfoCompetition: [...data.competitionInfoCompetition],
          quoteFilesToDelete: [],
          status: updatedStatus,
          dateSubmitted: isDraftSavedAsQuote ? new Date().toISOString() : quote.dateSubmitted,
          jobInfoStateName: (data.jobInfoState as US_State)?.name?.toUpperCase() ?? '',
          lastModifiedByEmail: userEmail,
          lastModifiedOn: currentDate,
        };
        await editQuoteMutation.mutateAsync(editableQuote);
      } else {
        const newQuote: QuoteDocumentWithoutId = {
          ...data,
          products: [...data.products?.map(product => ({ ...product, brand: data.brand }))],
          competitionInfoCompetition: [...data.competitionInfoCompetition],
          approvedBy: '',
          revisionApprovedBy: '',
          erp: '',
          quoteNumber: '',
          statusAndApprovalNotes: '',
          status: data.status === 'DRAFT' ? 'DRAFT' : 'SUBMITTED & RECEIVED',
          quoteFilesToDelete: [],
          dateSubmitted: data.status === 'DRAFT' ? '' : new Date().toISOString(),
          dateDeleted: '',
          dateRevised: '',
          dateApproved: '',
          jobInfoStateName: (data.jobInfoState as US_State)?.name?.toUpperCase() ?? '',
          createdByEmail: userEmail,
          createdOn: currentDate,
          lastModifiedByEmail: '',
          lastModifiedOn: '',
        };
        const savedNewQuote = await createQuoteMutation.mutateAsync(newQuote);
        const quoteDocumentation = [] as QuoteDoc[];
        if (data?.files) {
          for (let i = 0; i < data?.files?.length; i++) {
            const fileDate = formatDateTime(new Date().getTime());
            const isoDate = new Date().toISOString();
            handleFiles(data, i, userFullName, isoDate);
            quoteDocumentation.push({
              id: hashFileName(`${data.id}${data.files?.[i].name || ''}${isoDate}`),
              documentType: 'ADDITIONAL DOCUMENTATION',
              documentName: data.files?.[i].name || '',
              user: userFullName,
              uploadDate: fileDate,
            });
          }
        }
        const addedFilesQuote: QuoteDocument = {
          ...savedNewQuote,
          quoteDocumentation: quoteDocumentation,
        };
        await editQuoteMutation.mutateAsync(addedFilesQuote);
      }
      enqueueSnackbar('Quote was saved.', { variant: 'success' });
      history.push(ROUTES.quote);
    } catch (error) {
      enqueueSnackbar('Quote was not saved.', { variant: 'error' });
      console.error(error);
    }
  };

  const handleFiles = async (data: RepQuoteFormState, index: number, user: string, isoDate: string) => {
    const originalDocumentName = data.files?.[index].name || '';
    const fileId = hashFileName(`${data.id}${originalDocumentName}${isoDate}`);
    const formData = new FormData();
    const fileToUpload = data.files ? [data.files[index]] : [];
    formData.append('file', new File(fileToUpload, addPdfExtension(fileId)));

    await uploadPdfMutation.mutateAsync(formData);
  };

  const handleProductsError = () => {
    if (!fields.length && !isDraft) {
      setError('products', {
        message:
          'Product information is required. Do not forget to save the product info before submitting the quote request.',
        type: 'required',
      });
    } else {
      clearErrors('products');
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        aria-label='form'
        onSubmit={e => {
          e.preventDefault();
          handleProductsError();
          handleSubmit(onSubmit)(e);
        }}
      >
        <Box sx={{ padding: 2 }}>
          <QuotingNavigationHeader selectedTab='newQuote' />
          <Box sx={{ padding: 2 }}>
            <Typography align='center' variant='h4'>
              Quote Request Form
            </Typography>
            <Typography align='center' sx={alertStyles} variant='body2'>
              This form must be fully completed, submitted and approved PRIOR to a formal quote being issued; and PRIOR
              to the receipt of the customer's first order.
            </Typography>
            <Grid container justifyContent='space-between' spacing={{ xs: 2, md: 10 }}>
              <WholesalerInfo />
              <PlumberInfo />
            </Grid>
            <Box sx={{ paddingTop: { xs: 0, md: 3 } }}>
              <Grid container justifyContent='space-between' spacing={{ xs: 2, md: 10 }}>
                <JobInfo />
                <CompetitionInfo />
              </Grid>
            </Box>
            <Grid container>
              <ProductsInfo dateSubmitted={quote?.dateSubmitted} />
            </Grid>
            <Grid container>
              <QuoteDocumentation quote={quote} />
              <Notes quote={quote} />
              <LeaveFormDialog
                when={isDirtyAlt && !isSubmitted && !isSubmitting}
                methods={methods}
                onSubmit={onSubmit}
              />
            </Grid>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
}
